import { Link } from "react-router-dom";
import type { DashboardRecentOutput } from "@popcorn/shared/v1/dashboard";
import { ImageWithSkeleton } from "../ui/ImageWithSkeleton";
import styles from "./RecentOutputsStrip.module.css";

export function RecentOutputsStrip({
  outputs,
}: {
  outputs: readonly DashboardRecentOutput[];
}) {
  return (
    <section className={styles.section} aria-labelledby="recent-outputs-title">
      <div className={styles.header}>
        <h2 id="recent-outputs-title">Latest from your studio</h2>
        <Link to="/library">Browse library</Link>
      </div>

      {outputs.length === 0 ? (
        <p className={styles.empty}>
          Your finished videos will appear here once a run exports its first cut.
        </p>
      ) : (
        <div className={styles.strip}>
          {outputs.slice(0, 8).map((output) => (
            <Link
              className={styles.output}
              key={output.artifactId}
              to={`/projects/${encodeURIComponent(output.projectId)}#outputs`}
            >
              {output.thumbnailUrl ? (
                <ImageWithSkeleton
                  className={styles.thumb}
                  src={output.thumbnailUrl}
                  alt=""
                  loading="lazy"
                />
              ) : (
                <span className={`${styles.thumb} ${styles.emptyThumb}`}>Output</span>
              )}
              <span className={styles.outputBody}>
                <span className={styles.title}>{output.projectName}</span>
                <span className={styles.meta}>
                  {formatDate(output.createdAt)}
                  {output.durationSec ? ` - ${formatDuration(output.durationSec)}` : ""}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatDuration(seconds: number) {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainder = totalSeconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}
